import { useEffect, useRef, useState } from "react";
import { useVerificarOtpLoginUnificadoSpace } from "@/api/space/hooks/useSpace.authenticator";
import useAutenticacaoJwt from "../../../../../useAutenticacaoJwt";
import type { DesafioOtpLogin } from "../../loginUnificado.types";
import type useBloqueioTentativasLogin from "../../hooks/useBloqueioTentativasLogin";
import useTurnstileLogin from "../../hooks/useTurnstileLogin";
import { formatarEsperaLogin, obterMensagemErroLogin } from "../../utils/loginUnificado.utils";
import VerificacaoSegurancaLogin from "../VerificacaoSegurancaLogin";
import DialogoAutenticacaoDoisFatores from "../../../../AutenticacaoDoisFatores/DialogoAutenticacaoDoisFatores";

// --------------------------------- TYPES ---------------------------------
type DialogoOtpLoginUnificadoProps = {
	readonly desafio: DesafioOtpLogin;
	readonly bloqueio: ReturnType<typeof useBloqueioTentativasLogin>;
	readonly aoFechar: () => void;
	readonly aoExpirar: () => void;
};

// ------------------------------- COMPONENT -------------------------------
export default function DialogoOtpLoginUnificado({
	desafio,
	bloqueio,
	aoFechar,
	aoExpirar,
}: Readonly<DialogoOtpLoginUnificadoProps>) {
	// -------------------------------- STATES --------------------------------
	const [mensagemErro, setMensagemErro] = useState<string | undefined>(undefined);
	const controllerRef = useRef<AbortController | null>(null);
	const verificacao = useTurnstileLogin();
	const { concluirEntrarUnificado } = useAutenticacaoJwt();

	// -------------------------------- QUERIES -------------------------------
	const { mutateAsync, isPending } = useVerificarOtpLoginUnificadoSpace();

	// -------------------------------- EFFECTS -------------------------------
	useEffect(() => {
		function verificarPrazo() {
			if (Date.now() < desafio.expiraEm) return;
			controllerRef.current?.abort();
			aoExpirar();
		}
		verificarPrazo();
		const timer = globalThis.setInterval(verificarPrazo, 250);
		return () => {
			globalThis.clearInterval(timer);
			controllerRef.current?.abort();
		};
	}, [desafio.expiraEm, aoExpirar]);

	// ----------------------------- DERIVED STATE ----------------------------
	const erroExibido =
		bloqueio.segundosRestantes > 0
			? `Muitas tentativas sem sucesso. Aguarde ${formatarEsperaLogin(bloqueio.segundosRestantes)} para tentar de novo.`
			: mensagemErro;

	const verificacaoDisponivel =
		Boolean(verificacao.token) && bloqueio.segundosRestantes === 0 && Date.now() < desafio.expiraEm;

	// -------------------------------- HANDLERS ------------------------------
	async function verificar(codigo: string): Promise<void> {
		if (
			controllerRef.current ||
			bloqueio.estaBloqueado() ||
			Date.now() >= desafio.expiraEm ||
			!/^\d{6}$/.test(codigo)
		)
			return;

		const token = verificacao.consumirToken();
		if (!token) return;

		const controller = new AbortController();
		controllerRef.current = controller;
		setMensagemErro(undefined);

		try {
			const resposta = await mutateAsync({
				payload: { desafio_otp: desafio.valor, codigo_verificacao: codigo, token_turnstile: token },
				signal: controller.signal,
			});

			if (controller.signal.aborted) return;

			if (Date.now() >= desafio.expiraEm) return aoExpirar();
			const concluido = concluirEntrarUnificado?.({
				resposta,
				identificacao: desafio.identificacao,
				signal: controller.signal,
				versaoSessao: desafio.versaoSessao,
			});

			if (concluido) aoFechar();
		} catch (error: unknown) {
			if (controller.signal.aborted) return;

			bloqueio.registrarErro(error);
			setMensagemErro(obterMensagemErroLogin(error, "otp"));
			verificacao.renovar();
		} finally {
			if (controllerRef.current === controller) controllerRef.current = null;
		}
	}

	// -------------------------------- RETURN --------------------------------
	return (
		<DialogoAutenticacaoDoisFatores
			aberto
			desabilitarFechamentoBackdrop
			carregando={isPending}
			mensagemErro={erroExibido}
			verificacaoDisponivel={verificacaoDisponivel}
			aoFechar={aoFechar}
			aoVerificar={verificar}
			conteudoAdicional={
				<div className="mt-4">
					<VerificacaoSegurancaLogin verificacao={verificacao} carregando={isPending} action="login_otp" />
				</div>
			}
		/>
	);
}
